import { type RecordSourceSelectorProxy, ConnectionInterface } from 'relay-runtime';
import RelayDataID from '@atlassian/relay-data-id';

type UpdateStoreParamsType = {
	store: RecordSourceSelectorProxy;
	connectionId: string;
	sourceVersionAri: string;
	operationType: 'move-to-start' | 'move-to-end' | 'move-position';
	beforeVersionAri?: string;
};

export const updateStoreOnMove = ({
	store,
	connectionId,
	sourceVersionAri,
	operationType,
	beforeVersionAri,
}: UpdateStoreParamsType): void => {
	const connectionRecord = store.get(connectionId);
	if (connectionRecord == null) {
		return;
	}

	const sourceVersionDataId = RelayDataID({ id: sourceVersionAri }, 'JiraVersion') ?? '';
	const sourceVersionRecord = store.get(sourceVersionDataId);
	if (sourceVersionRecord == null) {
		return;
	}

	const { EDGES } = ConnectionInterface.get();
	const connectionEdges = connectionRecord.getLinkedRecords(EDGES) || [];
	let updatedEdges = [...connectionEdges];
	const indexOfEdgeToMove = updatedEdges.findIndex(
		(edge) => edge.getLinkedRecord('node')?.getValue('id') === sourceVersionAri,
	);
	const edgeToMove = updatedEdges.splice(indexOfEdgeToMove, 1)[0];

	switch (operationType) {
		case 'move-position': {
			// This is a bit confusing because we are sorting the versions connection by sequence
			// in descending order, whereas the "beforeKey" and "afterKey" values from the
			// DynamicTable onRankEnd callback assume that the record on the top of the table is
			// the "first" and therefore earliest record. This means that if we want to move an
			// edge to appear "after" another edge in terms of sequence (higher on the table), we
			// have to use the callback's "beforeKey" value
			const indexOfEdgeAfter = connectionEdges.findIndex(
				(edge) => edge.getLinkedRecord('node')?.getValue('id') === beforeVersionAri,
			);
			if (indexOfEdgeAfter > indexOfEdgeToMove) {
				updatedEdges.splice(indexOfEdgeAfter - 1, 0, edgeToMove);
			} else {
				updatedEdges.splice(indexOfEdgeAfter, 0, edgeToMove);
			}
			break;
		}
		case 'move-to-start': {
			// Again, the "start" in this case refers to the lowest sequence number, so the edge we
			// are moving needs to be added to the end of the connection
			updatedEdges = [...updatedEdges, edgeToMove];
			break;
		}
		case 'move-to-end': {
			updatedEdges = [edgeToMove, ...updatedEdges];
			break;
		}
		default:
			break;
	}

	connectionRecord.setLinkedRecords(updatedEdges, EDGES);
};
